import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import type { LabelFilter, ReviewLabel } from '../api/types';
import { UNLABELLED, emptyLabelFilter, isLabelFilterEmpty } from '../api/types';

interface Props {
  labels: ReviewLabel[];
  value: LabelFilter;
  onChange: (next: LabelFilter) => void;
}

type RowState = 'off' | 'show' | 'hide';

const nameOf = (key: string) => (key === UNLABELLED ? 'Unlabelled' : key);

/**
 * Label filter for the browse row: show any of these, hide any of those.
 *
 * SHOW is a union — "anything tagged pass-1 or pass-2" — with Unlabelled joining as one
 * more member. HIDE subtracts on top, which is the only way to say "tagged pass-1 but
 * NOT done"; a union alone can only ever widen a result.
 *
 * Each label owns ONE row with ONE state, so "show X and hide X" is unrepresentable
 * rather than merely discouraged. That's why this isn't two checkbox lists.
 *
 * Hidden labels are struck through instead of tinted red: ff-accent already means
 * "fail verdict" on every card below, so a red `done` row would read as a judgement
 * about cutout quality. Three non-colour cues carry the state (strike, ✕, muted name).
 *
 * Positioned absolutely inside a relative wrapper rather than portalled: the filter
 * row has no clipping ancestor, and staying in flow keeps the panel anchored while
 * the page scrolls. z-40 clears the sticky bulk toolbar (z-30) it can overlap.
 */
export function LabelFilterMenu({ labels, value, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocDown = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const stateOf = (key: string): RowState =>
    value.include.includes(key) ? 'show'
      : value.exclude.includes(key) ? 'hide'
        : 'off';

  // Dropping the key from BOTH lists before adding it back is what enforces the
  // one-row-one-state invariant — moving a label from SHOW to HIDE can't leave it in
  // both and produce a filter that matches nothing.
  const setRow = (key: string, next: RowState) => {
    const include = value.include.filter((v) => v !== key);
    const exclude = value.exclude.filter((v) => v !== key);
    if (next === 'show') include.push(key);
    if (next === 'hide') exclude.push(key);
    onChange({ include, exclude });
  };

  // The trigger has to read at a glance in a crowded row, so each side names its
  // label when it holds exactly one and falls back to a count when it holds more.
  const side = (keys: string[], noun: string): string | null =>
    keys.length === 0 ? null : keys.length === 1 ? nameOf(keys[0]) : `${keys.length} ${noun}`;
  const shown = side(value.include, 'shown');
  const hidden = side(value.exclude, 'hidden');

  const summary: ReactNode = (() => {
    if (isLabelFilterEmpty(value)) return 'All labels';
    const parts: ReactNode[] = [];
    if (shown) parts.push(<span key="s">{shown}</span>);
    if (hidden) {
      // strike the NAME (it's the label being crossed off); never a bare count
      parts.push(value.exclude.length === 1
        ? <span key="h" className="line-through decoration-ff-forest/60">{hidden}</span>
        : <span key="h">{hidden}</span>);
    }
    return parts.length === 2
      ? <>{parts[0]}<span className="opacity-40 mx-1">·</span>{parts[1]}</>
      : parts[0];
  })();

  // A strikethrough is invisible to a screen reader, so the formula is also spelled out.
  const spoken = isLabelFilterEmpty(value)
    ? 'Filter by review label'
    : 'Filter by review label: '
      + [shown && `showing ${shown}`, hidden && `hiding ${hidden}`]
        .filter(Boolean).join(', ');

  const cell =
    'w-8 h-6 flex items-center justify-center text-[11px] leading-none ' +
    'transition-colors cursor-pointer focus:outline-none focus:ring-2 ' +
    'focus:ring-inset focus:ring-ff-forest/25';

  const row = (key: string, label: ReactNode, trailing: ReactNode, spokenName: string) => {
    const st = stateOf(key);
    return (
      <div key={key}
           className="flex items-center gap-2.5 px-1.5 py-1 rounded-lg hover:bg-ff-mint/25">
        <span className="inline-flex shrink-0 rounded-md border border-ff-sand overflow-hidden">
          <button
            type="button"
            aria-pressed={st === 'show'}
            aria-label={`Show ${spokenName}`}
            onClick={() => setRow(key, st === 'show' ? 'off' : 'show')}
            className={cell + (st === 'show'
              ? ' bg-ff-mint text-ff-forest'
              : ' text-ff-mute/40 hover:bg-ff-mint/40 hover:text-ff-forest')}
          >✓</button>
          <button
            type="button"
            aria-pressed={st === 'hide'}
            aria-label={`Hide ${spokenName}`}
            onClick={() => setRow(key, st === 'hide' ? 'off' : 'hide')}
            className={cell + ' border-l border-ff-sand ' + (st === 'hide'
              ? 'bg-ff-sand/70 text-ff-ink'
              : 'text-ff-mute/40 hover:bg-ff-sand/40 hover:text-ff-ink')}
          >✕</button>
        </span>
        <span className={'truncate text-xs ' + (st === 'hide'
          ? 'line-through decoration-ff-mute/70 text-ff-mute'
          : 'text-ff-ink')}>{label}</span>
        <span className="ml-auto shrink-0 text-[10px] text-ff-mute tabular-nums">
          {trailing}
        </span>
      </div>
    );
  };

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        data-testid="label-filter"
        aria-haspopup="true"
        aria-expanded={open}
        aria-label={spoken}
        onClick={() => setOpen((o) => !o)}
        title={'Show any of the ticked labels, then hide anything carrying a crossed-out '
          + 'one — "tagged pass-1 but not done". Unlabelled means not yet triaged.'}
        className={
          'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border ' +
          'transition-colors focus:outline-none focus:ring-2 focus:ring-ff-forest/20 ' +
          (isLabelFilterEmpty(value)
            ? 'border-ff-sand bg-white text-ff-ink hover:border-ff-forest/40'
            : 'border-ff-forest bg-ff-mint/50 text-ff-forest')
        }
      >
        <span className="max-w-[11rem] truncate">{summary}</span>
        <span aria-hidden className="text-[9px] opacity-60">▾</span>
      </button>

      {open && (
        <div
          role="group"
          aria-label="Review labels"
          data-testid="label-filter-menu"
          className="absolute left-0 top-full mt-2 z-40 w-72 ff-card p-2 max-h-80 overflow-y-auto"
        >
          <div className="flex items-center gap-2.5 px-1.5 pb-1
                          text-[9px] font-mono uppercase tracking-widish text-ff-mute">
            <span className="flex shrink-0">
              <span className="w-8 text-center">Show</span>
              <span className="w-8 text-center">Hide</span>
            </span>
            <span>Label</span>
          </div>

          {row(UNLABELLED,
               <span className="font-medium text-ff-forest">Unlabelled</span>,
               'not triaged',
               'untriaged items')}

          {labels.length > 0 && <div className="ff-divider-rule my-1.5" />}

          {labels.map((l) => row(l.label, l.label, l.count, `items tagged ${l.label}`))}

          {labels.length === 0 && (
            <p className="px-2.5 py-2 text-[11px] text-ff-mute">
              No labels yet — apply one below to start tracking your progress.
            </p>
          )}

          {!isLabelFilterEmpty(value) && (
            <>
              <div className="ff-divider-rule my-1.5" />
              <button
                type="button"
                data-testid="label-filter-clear"
                onClick={() => onChange(emptyLabelFilter())}
                className="w-full px-2.5 py-1.5 rounded-lg text-left text-[11px] font-mono uppercase tracking-widish text-ff-mute hover:text-ff-forest hover:bg-ff-mint/40 transition-colors"
              >
                Clear
              </button>
            </>
          )}
        </div>
      )}
    </div>
  );
}
